/**
 * Persistence layer for the Call Script column.
 *
 * Owns BOTH debounced server writes (the working output and the heard markers),
 * the `beforeunload` guard, and the unmount flush + finalize, built on the generic
 * `useDebouncedSave` hook. Behaviour is intentionally identical to the inline
 * implementation it replaced — see the per-method notes for the load-bearing
 * (HIPAA data-loss) semantics.
 */
import type { CallHeardMarker } from '@compass/client/types/call';
import { Action } from '@compass/client/types/case_record';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import { useUpdateCall } from '@/hooks/useCalls';
import { useDebouncedSave } from '@/hooks/useDebouncedSave';
import { caseRecordService } from '@/services/api';
import { useStore } from '@/store/index';

import type { HeardMap } from './heard';
import { heardMapToMarkers } from './heard';

/** Debounce window for persisting edits to the call (coalesces rapid keystrokes). */
const SAVE_DEBOUNCE_MS = 600;

export interface UseHumanCallPersistenceArgs {
  caseId: string;
  /**
   * The SELECTED call's id — the call whose output_data/heard_markers we persist. Selected,
   * not live, so the write-up after hang-up lands on the call it belongs to.
   *
   * Undefined only on the pre-call preview, where the script is read-only
   * (`humanCallEditable` requires a call id), so the guards below never drop a real edit.
   */
  callId: string | undefined;
}

export interface HumanCallPersistence {
  /** Mark the session dirty and schedule a debounced output save of `next`. */
  scheduleOutputSave: (next: Record<string, unknown>) => void;
  /** The freshest pending output (clone base for onFieldChange), or null. */
  peekPendingOutput: () => Record<string, unknown> | null;
  /** Map the heard set via heardMapToMarkers(map, currentUserId) + schedule a save. */
  scheduleHeardSave: (map: HeardMap) => void;
  /** updateCall.isPending — drives the "Saving…" indicator. */
  isSaving: boolean;
}

export function useHumanCallPersistence({
  caseId,
  callId,
}: UseHumanCallPersistenceArgs): HumanCallPersistence {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const currentUserId = useStore(state => state.user?.id);

  // Single shared mutation for both saves; its pending flag drives "Saving…".
  const updateCall = useUpdateCall();

  // True once any output edit happened this session — gates the unmount finalize
  // so merely opening + leaving the tab doesn't fire a redundant recompute.
  const dirtyRef = useRef(false);

  // Silent persist: POST the whole output_data for this call. Keep an error toast
  // only — no success toast. On error, show the toast AND rethrow so the debounced
  // save keeps the value pending (the beforeunload guard stays armed).
  const persistOutput = useCallback(
    async (output: Record<string, unknown>) => {
      if (!caseId || !callId) return;
      try {
        await updateCall.mutateAsync({
          caseRecordId: caseId,
          callId,
          data: { output_data: output },
        });
      } catch (error) {
        const message =
          (error as { response?: { data?: { message?: string } }; message?: string })?.response
            ?.data?.message ||
          (error as { message?: string })?.message ||
          'Failed to save call output data';
        toast.error(message);
        throw error;
      }
    },
    [caseId, callId, updateCall],
  );

  // Silent persist for heard markers. No try/catch and no toast: a rejection
  // propagates to the debounced save, which keeps the value pending (localStorage
  // retains the marks meanwhile). Mirrors the original behaviour.
  const persistHeard = useCallback(
    async (markers: Record<string, CallHeardMarker>) => {
      if (!caseId || !callId) return;
      await updateCall.mutateAsync({
        caseRecordId: caseId,
        callId,
        data: { heard_markers: markers },
      });
    },
    [caseId, callId, updateCall],
  );

  const outputSave = useDebouncedSave(SAVE_DEBOUNCE_MS, persistOutput);
  const heardSave = useDebouncedSave(SAVE_DEBOUNCE_MS, persistHeard);

  const scheduleOutputSave = useCallback(
    (next: Record<string, unknown>) => {
      dirtyRef.current = true;
      outputSave.schedule(next);
    },
    [outputSave],
  );

  const peekPendingOutput = outputSave.peek;

  const scheduleHeardSave = useCallback(
    (map: HeardMap) => {
      heardSave.schedule(heardMapToMarkers(map, currentUserId));
    },
    [heardSave, currentUserId],
  );

  // --- Finalize: reconcile persisted disposition on unmount ---------------
  const finalizeMutation = useMutation({
    mutationFn: () => {
      if (!selectedOrganizationId || !selectedProgramId) {
        return Promise.reject(new Error('Organization and program context required'));
      }
      return caseRecordService.executeAction({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_record_id: caseId,
        action: Action.ACTION_CALCULATE_DISPOSITION,
      });
    },
    // Non-fatal: a reconcile failure must not interrupt the user. The live UI
    // never blocks on the persisted disposition.
    onError: () => {},
  });

  // Keep stable refs so the `[]`-dep effects below don't re-run on every render.
  const finalizeRef = useRef(finalizeMutation);
  finalizeRef.current = finalizeMutation;
  const outputSaveRef = useRef(outputSave);
  outputSaveRef.current = outputSave;
  const heardSaveRef = useRef(heardSave);
  heardSaveRef.current = heardSave;

  // Guard a hard browser close/navigation while a write is still unsaved — either a
  // pending output edit OR a pending heard-marker write (each is non-null between its
  // edit and a successful flush, and stays non-null if a flush FAILED). The heard
  // case matters: on call-end the local heard cache is cleared, so a heard POST lost
  // to a hard close would never reach the authoritative backend. The browser shows its
  // native "leave site?" prompt so the worker doesn't silently lose captured call data.
  // In-app navigation is handled by the unmount effect below (flush + finalize); this
  // only covers full-page unloads, where an async flush can't complete in time.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (outputSaveRef.current.hasPending() || heardSaveRef.current.hasPending()) {
        e.preventDefault();
        // Legacy assignment required by some browsers to trigger the prompt.
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    // On unmount (tab close / call leaves IN_PROGRESS, i.e. the call disconnects):
    // flush any pending output, THEN fire ACTION_CALCULATE_DISPOSITION so hermes
    // recomputes against the final output (not a pre-flush snapshot). Skip the
    // recompute entirely when the session made no edits — merely opening the tab
    // shouldn't trigger it.
    //
    // This effect does NOT touch localStorage: the working output is never
    // mirrored there (`persistToLocalStorage: false`), and the heard marks (keys
    // + timestamps only) are cleared on the call-end transition by
    // DefaultCaseDetailsPage — which is always mounted during the case, so it
    // fires regardless of which tab is active (this tab unmounts on tab-switch).
    return () => {
      const finalize = () => finalizeRef.current.mutate();
      if (outputSaveRef.current.hasPending()) {
        // flush() clears the timer and persists the freshest pending output; it
        // always resolves (the runner swallows errors). Finalize after the flush
        // either way so hermes recomputes against the freshest output.
        Promise.resolve(outputSaveRef.current.flush()).then(finalize, finalize);
      } else if (dirtyRef.current) {
        // Edits were already debounce-flushed this session — reconcile disposition.
        finalize();
      }
      // Flush any pending heard-marker write (mirrors the output flush above).
      void heardSaveRef.current.flush();
    };
  }, []);

  return useMemo(
    () => ({
      scheduleOutputSave,
      peekPendingOutput,
      scheduleHeardSave,
      isSaving: updateCall.isPending,
    }),
    [scheduleOutputSave, peekPendingOutput, scheduleHeardSave, updateCall.isPending],
  );
}
